<template>
  <div class="grid grid-cols-2 gap-4 mb-10">
    <button
      @click="onGoogleLogin"
      :disabled="isSocialLoading"
      class="flex items-center justify-center gap-3 py-4 bg-white/5 border border-white/10 rounded-2xl transition-all active:scale-95 disabled:opacity-60 disabled:pointer-events-none hover:bg-white/10 hover:border-white/20 group cursor-pointer"
    >
      <img
        src="https://www.svgrepo.com/show/475656/google-color.svg"
        class="w-5 h-5 transition-opacity"
        :class="{ 'opacity-40': isGoogleLoading }"
        alt="Google"
      />
      <div class="relative flex items-center justify-center min-w-[50px] h-5">
        <span v-if="isGoogleLoading" class="animate-spin text-white/80">
          <Loader2 :size="16" />
        </span>
        <span
          v-else
          class="text-[11px] font-black text-white tracking-widest uppercase"
        >
          Google
        </span>
      </div>
    </button>

    <button
      type="button"
      :disabled="isSocialLoading"
      @click="onAppleLogin"
      class="flex items-center justify-center gap-3 py-4 bg-white/5 border border-white/10 rounded-2xl transition-all active:scale-95 disabled:opacity-60 disabled:pointer-events-none hover:bg-white/10 hover:border-white/20 group cursor-pointer"
    >
      <img
        src="https://www.svgrepo.com/show/511330/apple-173.svg"
        class="w-5 h-5 invert transition-opacity"
        :class="
          isAppleLoading ? 'opacity-30' : 'opacity-70 group-hover:opacity-100'
        "
        alt="Apple"
      />
      <div class="relative flex items-center justify-center min-w-[50px] h-5">
        <span v-if="isAppleLoading" class="animate-spin text-white/80">
          <Loader2 :size="16" />
        </span>
        <span
          v-else
          class="text-[11px] font-black text-white tracking-widest uppercase"
        >
          Apple
        </span>
      </div>
    </button>
  </div>
</template>

<script lang="ts" setup>
import { useSocialIdentity } from "@/composables/auth/useSocialIdentity";
import { Loader2 } from "lucide-vue-next";

const { signInWithGoogle, signInWithApple } = useSocialIdentity();
const { showErrorToast } = useNotification();

const { socialLogin } = useAuth();

const route = useRoute();

const isSocialLoading = ref(false);
const isGoogleLoading = ref(false);
const isAppleLoading = ref(false);

const handleSocialCredential = async (jwt: string): Promise<void> => {
  try {
    await socialLogin(
      { jwt },
      (route.query.redirect as string | undefined) || "/profiles",
    );
  } catch (error: any) {
    showErrorToast(
      "Social login failed",
      error?.message || "Please try again.",
    );
  }
};

const onGoogleLogin = async (): Promise<void> => {
  if (isSocialLoading.value) return;

  isSocialLoading.value = true;
  isGoogleLoading.value = true;

  try {
    const jwt = await signInWithGoogle();
    await handleSocialCredential(jwt);
  } catch (error: any) {
    // Don't toast on user-initiated cancellations
    if (!error?.message?.includes("cancelled")) {
      showErrorToast(
        "Google login failed",
        error?.message || "Please try again.",
      );
    }
  } finally {
    isSocialLoading.value = false;
    isGoogleLoading.value = false;
  }
};

const onAppleLogin = async (): Promise<void> => {
  if (isSocialLoading.value) return;

  isSocialLoading.value = true;
  isAppleLoading.value = true;

  try {
    const jwt = await signInWithApple();
    await handleSocialCredential(jwt);
  } catch (error: any) {
    // Apple throws a specific error object with error.error = "popup_closed_by_user"
    const isCancelled =
      error?.error === "popup_closed_by_user" ||
      error?.message?.includes("cancelled");

    if (!isCancelled) {
      showErrorToast(
        "Apple login failed",
        error?.message || "Please try again.",
      );
    }
  } finally {
    isSocialLoading.value = false;
    isAppleLoading.value = false;
  }
};
</script>

<style></style>
